import {CircularProgress, Paper, Tooltip, Typography, Button, IconButton} from "@mui/material";
import {DataGrid, GridColDef, } from "@mui/x-data-grid";
import { useQueryAsUser, useMutationAsUser } from "../../clients/graphql/graphql.hooks";
import {StockSymbolComponent} from "../stocks/StockSymbolComponent";
import React, { useState } from "react";
import Box from "@mui/material/Box";
import {jediPortfolioRestrictionsGet, PortfolioRestriction, PORTFOLIO_RESTRICTION_DELETE, PORTFOLIO_RESTRICTIONS_DELETE_ALL} from "../../clients/graphql/components/tables-queries";
import {formatMoment} from "../../functions/momentFormatter";
import DeleteIcon from '@mui/icons-material/Delete';

export const PortfolioRestrictionsTable: React.FC<{ userKey: number, portfolioKey: number}> = ({ userKey, portfolioKey }) => {
    const response = useQueryAsUser(jediPortfolioRestrictionsGet, { userKey, portfolioKey, variables: { userKey, portfolioKey } });
    const data = response.data?.portfolioGet?.portfolio?.restrictions || [];
    const sorted = [...data].sort((a: PortfolioRestriction, b: PortfolioRestriction) => a?.asset?.symbol.localeCompare(b?.asset?.symbol));
    const error = response.error;
    const loading = response.loading;
    const [deleting, setDeleting] = useState(false);

    const [removeRestriction] = useMutationAsUser(PORTFOLIO_RESTRICTION_DELETE, { userKey });
    const [removeAllRestrictions] = useMutationAsUser(PORTFOLIO_RESTRICTIONS_DELETE_ALL, { userKey });

    const handleRemove = async (assetKey: number, symbol: string) => {
        if (!window.confirm(`Remove restriction for ${symbol}?`)) return;
        setDeleting(true);
        try {
            await removeRestriction({ variables: { portfolioKey, assetKey } });
            await response.refetch();
        } catch (err) {
            console.error('Failed to remove restriction:', err);
            alert('Failed to remove restriction. Please try again.');
        } finally {
            setDeleting(false);
        }
    };

    const handleRemoveAll = async () => {
        if (!window.confirm(`Remove ALL ${sorted.length} restriction(s) for this portfolio?`)) return;
        setDeleting(true);
        try {
            await removeAllRestrictions({ variables: { portfolioKey } });
            await response.refetch();
        } catch (err) {
            console.error('Failed to remove all restrictions:', err);
            alert('Failed to remove all restrictions. Please try again.');
        } finally {
            setDeleting(false);
        }
    };

    if (loading) {
        return <CircularProgress sx={{marginLeft: 'calc(50% - 10px)'}}/>
    }

    if (error) {
        return <div>Error fetching data. Please reload page</div>;
    }

    const columns: GridColDef[] = [
        {
            field: 'symbol',
            headerName: 'Symbol',
            width: 120,
            renderCell: (params) => (
                <StockSymbolComponent symbol={params.row.symbol} logo={params.row.pictureUrl}
                                      value={params.row.symbol}/>
            )
        },
        {
            field: 'createdAt',
            headerName: 'Created At',
            width: 150,
            valueFormatter: (value) => value ? formatMoment(value) : ''
        },
        {
            field: 'reason',
            headerName: 'Reason',
            width: 350,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%'}}>
                    <Tooltip title={portfolioRestrictionExplaination(params.row.reason)}>
                        <Typography
                            title={portfolioRestrictionExplaination(params.row.reason)}
                            style={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                fontSize: 'small',
                            }}>{portfolioRestrictionExplaination(params.row.reason)}</Typography>
                    </Tooltip>
                </div>
            ),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 100,
            sortable: false,
            renderCell: (params) => (
                <IconButton
                    size="small"
                    color="error"
                    disabled={deleting}
                    onClick={() => handleRemove(params.row.assetKey, params.row.symbol)}
                    title="Remove restriction"
                >
                    <DeleteIcon fontSize="small" />
                </IconButton>
            ),
        },
    ];

    const rows = sorted.map((row: PortfolioRestriction, index: number) => {
        return {
            id: index,
            assetKey: row.assetKey,
            symbol: row.asset.symbol,
            createdAt: row.createdAt,
            reason: row.reason,
            pictureUrl: row.asset.pictureUrl
        }
    });

    return <Paper elevation={1} sx={{
        width: '100%',
        padding: 2,
        height: 1,
    }}><Box style={{height: '22rem', width: 'auto'}}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '.75rem' }}>
            <Typography align={'left'} sx={{
                color: '#6D6D6D',
                font: 'Roboto',
                fontWeight: 500,
                fontSize: '13px',
                lineHeight: '12px',
                letterSpacing: '0.05em',
            }}>
                PORTFOLIO RESTRICTIONS
            </Typography>
            {sorted.length > 0 && (
                <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    disabled={deleting}
                    onClick={handleRemoveAll}
                    startIcon={<DeleteIcon />}
                >
                    Remove All
                </Button>
            )}
        </Box>
        <DataGrid
            rows={rows}
            columns={columns}
            rowHeight={40}
            loading={loading}
            hideFooter={true}
            sx={{
                height: '19rem'
            }}
        />
    </Box></Paper>
}

export const portfolioRestrictionExplaination = (reason: string) => {
    switch (reason) {
        case 'UNSIGNED_DESIGNATED_INVESTMENTS_AGREEMENT':
            return `
                Security is unable to be traded without the customer
                signing a Designated Investment Agreement in their
                brokerage account
            `;
        default:
            return "Unknown";
    }
}